import React, { useDeferredValue, useEffect, useMemo } from 'react';
import { type NavigateOptions, type To, useLocation, useParams } from 'react-router-dom';
import CurrencyDisplay from '../components/AmountDisplay';
import ScreenContainer from '../components/ScreenContainer';
import TransactionList from '../components/TransactionList';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchTransactionRange } from '../store/slices/transactionsSlice';
import { fetchTags, selectTagMap } from '../store/slices/tagsSlice';
import type { Transaction } from '../types';
import { isInvestmentDebitTransaction } from '../utils/cashFlowUtils';
import { parseTransactionDateParts } from '../utils/dateTimeUtils';
import { getDescendantTagNames, normalizeTagKey, normalizeTagName, UNTAGGED_KEY } from '../utils/tagHelpers';

type CashFlowType = 'incoming' | 'outgoing' | 'investment' | 'all';

const CASH_FLOW_LABELS: Record<CashFlowType, string> = {
  incoming: 'Incoming',
  outgoing: 'Outgoing',
  investment: 'Investment',
  all: 'Transactions',
};

const getMonthName = (year: number, month: number) => {
  return new Date(year, month - 1).toLocaleString('default', { month: 'long' });
};

const isValidCashFlowType = (value: string): value is CashFlowType => {
  return value === 'incoming' || value === 'outgoing' || value === 'investment' || value === 'all';
};

const doesMonthDataExistInStore = (
  availableMonths: { [year: string]: { [month: number]: boolean } },
  year: number,
  month: number
): boolean => {
  return availableMonths[String(year)]?.[month] === true;
};

interface CashFlowTransactionsRouteState {
  categoryTagName?: string;
  periodScope?: 'month' | 'year';
}

interface CashFlowTransactionsScreenProps {
  getTransactionDetailPath?: (transaction: Transaction) => To;
  getTransactionSplitPath?: (transaction: Transaction) => To;
  getTransactionNavigationState?: (transaction: Transaction) => NavigateOptions['state'];
}

const filterTransactions = (
  transactions: Transaction[],
  tagMap: ReturnType<typeof selectTagMap>,
  type: CashFlowType,
  year: number,
  month: number,
  categoryTagNames: Set<string> | null,
  isYearScope: boolean
) => {
  return transactions.filter((tx) => {
    const txDate = parseTransactionDateParts(tx.transactionDate);
    const isInSelectedPeriod = isYearScope
      ? txDate.year === year
      : txDate.year === year && txDate.month === month;
    if (!isInSelectedPeriod) {
      return false;
    }

    if (categoryTagNames) {
      if (tx.type !== 'DEBIT' || isInvestmentDebitTransaction(tx, tagMap)) {
        return false;
      }
      return categoryTagNames.has(normalizeTagKey(tx.tag));
    }

    if (type === 'all') {
      return true;
    }

    if (type === 'incoming') {
      return tx.type === 'CREDIT';
    }

    if (type === 'outgoing') {
      return tx.type === 'DEBIT' && !isInvestmentDebitTransaction(tx, tagMap);
    }

    if (type === 'investment') {
      return isInvestmentDebitTransaction(tx, tagMap);
    }

    return false;
  });
};

const doesRangeDataExistInStore = (
  availableMonths: { [year: string]: { [month: number]: boolean } },
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number
): boolean => {
  let current = new Date(startYear, startMonth - 1, 1);
  const end = new Date(endYear, endMonth - 1, 1);

  while (current <= end) {
    const year = current.getFullYear();
    const month = current.getMonth() + 1;
    if (!doesMonthDataExistInStore(availableMonths, year, month)) {
      return false;
    }
    current.setMonth(current.getMonth() + 1);
  }

  return true;
};

const CashFlowTransactionsScreen: React.FC<CashFlowTransactionsScreenProps> = ({
  getTransactionDetailPath,
  getTransactionSplitPath,
  getTransactionNavigationState,
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const routeState = location.state as CashFlowTransactionsRouteState | null;
  const { flowType, year, month } = useParams<{
    flowType: string;
    year: string;
    month: string;
  }>();
  const { transactions, status, availableMonths } = useAppSelector((state) => state.transactions);
  const { tags, status: tagsStatus, error: tagsError } = useAppSelector((state) => state.tags);
  const tagMap = useAppSelector(selectTagMap);
  const deferredTransactions = useDeferredValue(transactions);
  const deferredTagMap = useDeferredValue(tagMap);

  const parsedYear = Number(year);
  const parsedMonth = Number(month);
  const normalizedFlowType = (flowType ?? '').toLowerCase();

  const isYearValid = Number.isInteger(parsedYear) && parsedYear > 1900;
  const isMonthValid = Number.isInteger(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12;
  const isFlowTypeValid = isValidCashFlowType(normalizedFlowType);
  const isRouteValid = isYearValid && isMonthValid && isFlowTypeValid;
  const flowTypeForFiltering: CashFlowType = isFlowTypeValid ? normalizedFlowType : 'all';
  const periodScope = routeState?.periodScope;
  const isYearScope = periodScope === 'year';
  const categoryTagName = routeState?.categoryTagName;
  const hasCategoryFilter = !!(categoryTagName && categoryTagName.trim());
  const requiresTagHierarchy = hasCategoryFilter || normalizedFlowType === 'outgoing' || normalizedFlowType === 'investment';

  const categoryTagNames = useMemo(() => {
    if (!hasCategoryFilter || !categoryTagName) {
      return null;
    }
    if (normalizeTagName(categoryTagName) === 'untagged') {
      return new Set<string>([UNTAGGED_KEY]);
    }
    return getDescendantTagNames(tags, categoryTagName);
  }, [categoryTagName, hasCategoryFilter, tags]);

  const resolvedCategoryName = useMemo(() => {
    if (!hasCategoryFilter || !categoryTagName) {
      return null;
    }
    if (normalizeTagName(categoryTagName) === 'untagged') {
      return 'Untagged';
    }
    const matchingTag = tags.find((tag) => normalizeTagName(tag.name) === normalizeTagName(categoryTagName));
    return matchingTag?.name ?? categoryTagName ?? null;
  }, [categoryTagName, hasCategoryFilter, tags]);

  useEffect(() => {
    if (tagsStatus === 'idle') {
      dispatch(fetchTags());
    }
  }, [dispatch, tagsStatus]);

  useEffect(() => {
    if (!isRouteValid) {
      return;
    }

    if (status === 'loading' || status === 'loadingMore') {
      return;
    }

    dispatch(fetchTransactionRange({
      startYear: parsedYear,
      startMonth: isYearScope ? 1 : parsedMonth,
      endYear: parsedYear,
      endMonth: isYearScope ? 12 : parsedMonth,
    }));
  }, [dispatch, isRouteValid, isYearScope, parsedMonth, parsedYear, status]);

  const title = hasCategoryFilter
    ? (resolvedCategoryName ?? 'Category Transactions')
    : CASH_FLOW_LABELS[flowTypeForFiltering];
  const monthName = isRouteValid ? getMonthName(parsedYear, parsedMonth) : '';
  const selectedRangeLoaded = isRouteValid && (isYearScope
    ? doesRangeDataExistInStore(availableMonths, parsedYear, 1, parsedYear, 12)
    : doesMonthDataExistInStore(availableMonths, parsedYear, parsedMonth));
  const isTagsLoading = requiresTagHierarchy && (tagsStatus === 'idle' || tagsStatus === 'loading');
  const hasTagsError = requiresTagHierarchy && tagsStatus === 'failed';
  const isLoading =
    ((status === 'loading' || status === 'loadingMore') && !selectedRangeLoaded) ||
    isTagsLoading;

  const transactionsForPeriod = useMemo(() => filterTransactions(
    !isRouteValid || hasTagsError
      ? []
      : deferredTransactions,
    deferredTagMap,
    flowTypeForFiltering,
    parsedYear,
    parsedMonth,
    categoryTagNames,
    isYearScope
  ), [
    isRouteValid,
    hasTagsError,
    deferredTransactions,
    deferredTagMap,
    flowTypeForFiltering,
    parsedYear,
    parsedMonth,
    categoryTagNames,
    isYearScope
  ]);

  const netAmount = useMemo(() => transactionsForPeriod.reduce((total, transaction) => {
    if (transaction.excludeFromAccounting) {
      return total;
    }

    if (transaction.type === 'DEBIT') {
      return total - Math.abs(transaction.amount);
    }

    return total + Math.abs(transaction.amount);
  }, 0), [transactionsForPeriod]);
  const netAmountType = netAmount < 0 ? 'DEBIT' : 'CREDIT';
  const monthLabel = isYearScope ? `YEAR ${parsedYear}` : `${monthName.toUpperCase()} ${parsedYear}`;

  if (!isRouteValid) {
    return (
      <ScreenContainer title="Cash Flow">
        <div className="w-full max-w-sm px-6 pt-12 text-center text-muted-foreground">
          Invalid cash flow transaction view.
        </div>
      </ScreenContainer>
    );
  }

  if (hasTagsError) {
    return (
      <ScreenContainer title={title}>
        <div className="h-full flex items-start justify-center pt-4 px-6 text-center text-sm text-muted-foreground">
          Failed to load tags: {tagsError}
        </div>
      </ScreenContainer>
    );
  }
  const listTopContent = (
    <div className="pt-2 px-4">
      <div className="rounded-[28px] border border-border bg-secondary/40 px-5 py-4">
        <p className="text-[0.7rem] tracking-[0.18em] font-semibold text-muted-foreground uppercase">
          {monthLabel}
        </p>
        <div className="border-t border-border/80 my-3" />
        <CurrencyDisplay
          amount={netAmount}
          type={netAmountType}
          className="text-4xl font-semibold tracking-tight"
          showFraction={false}
          smallRupeeSymbol={true}
        />
      </div>
    </div>
  );

  return (
    <ScreenContainer title={title}>
      <div className="flex flex-col w-full items-center h-full">
        <div className="w-full max-w-sm flex-1 min-h-0">
          {isLoading ? (
            <div className="h-full flex items-start justify-center pt-4 px-6 text-center text-sm text-muted-foreground">
              Loading transactions...
            </div>
          ) : transactionsForPeriod.length > 0 ? (
            <TransactionList
              transactions={transactionsForPeriod}
              className="h-full"
              topContent={listTopContent}
              nestedSheetZIndex={70}
              getTransactionDetailPath={getTransactionDetailPath}
              getTransactionSplitPath={getTransactionSplitPath}
              getTransactionNavigationState={getTransactionNavigationState}
            />
          ) : (
            <div className="h-full px-4">
              {listTopContent}
              <div className="flex items-start justify-center pt-4 px-6 text-center text-sm text-muted-foreground">
                No transactions found.
              </div>
            </div>
          )}
        </div>
      </div>
    </ScreenContainer>
  );
};

export default CashFlowTransactionsScreen;
